var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as PropTypes from 'prop-types';
import * as _ from 'lodash';
import { Popper } from 'react-popper';
import { childrenExist, AutoControlledComponent, EventStack, isBrowser, commonPropTypes, } from '../../lib';
import Ref from '../Ref/Ref';
import { getPopupPlacement, applyRtlToOffset } from './positioningHelper';
import PopupContent from './PopupContent';
import { popupBehavior } from '../../lib/accessibility';
import { FocusTrapZone } from '../../lib/accessibility/FocusZone';
var POSITIONS = ['above', 'below', 'before', 'after'];
var ALIGNMENTS = ['top', 'bottom', 'start', 'end', 'center'];
/**
 * A Popup displays additional information on top of a page.
 * @accessibility This is example usage of the accessibility tag.
 * This should be replaced with the actual description after the PR is merged
 */
var Popup = /** @class */ (function (_super) {
    __extends(Popup, _super);
    function Popup() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.outsideClickSubscription = EventStack.noSubscription;
        _this.triggerDomElement = null;
        _this.popupDomElement = null;
        _this.actionHandlers = {
            toggle: function (e) {
                _this.trySetOpen(!_this.state.open, e, true);
            },
            closeAndFocusTrigger: function (e) {
                _this.closeAndFocusTrigger(e);
                e.stopPropagation();
            },
        };
        _this.closeAndFocusTrigger = function (e) {
            if (_this.state.open) {
                _this.trySetOpen(false, e, true);
                _.invoke(_this.triggerDomElement, 'focus');
            }
        };
        _this.renderPopperChildren = function (popupPositionClasses, rtl, accessibility, _a) {
            var ref = _a.ref, popupPlacementStyles = _a.style;
            var content = _this.props.content;
            var popupWrapperAttributes = __assign({}, (rtl && { dir: 'rtl' }), accessibility.attributes.popup, accessibility.keyHandlers.popup, { className: popupPositionClasses, style: popupPlacementStyles });
            var focusTrapProps = __assign({}, (typeof accessibility.focusTrap === 'boolean' ? {} : accessibility.focusTrap), popupWrapperAttributes);
            /**
             * if there is no focus trap wrapper, we should apply
             * HTML attributes and positioning to popup content directly
             */
            var popupContentAttributes = accessibility.focusTrap ? {} : popupWrapperAttributes;
            var popupContent = React.isValidElement(content)
                ? React.cloneElement(content, popupContentAttributes)
                : Popup.Content.create(content, {
                    defaultProps: popupContentAttributes,
                });
            return (React.createElement(Ref, { innerRef: function (domElement) {
                    ref(domElement);
                    _this.popupDomElement = domElement;
                } }, accessibility.focusTrap ? (React.createElement(FocusTrapZone, __assign({}, focusTrapProps), popupContent)) : (popupContent)));
        };
        return _this;
    }
    Popup.prototype.updateOutsideClickSubscription = function () {
        var _this = this;
        this.outsideClickSubscription.unsubscribe();
        if (this.state.open) {
            setTimeout(function () {
                _this.outsideClickSubscription = EventStack.subscribe('click', function (e) {
                    var isOutsidePopupElement = _this.popupDomElement && !_this.popupDomElement.contains(e.target);
                    var isOutsideTriggerElement = _this.triggerDomElement && !_this.triggerDomElement.contains(e.target);
                    if (isOutsidePopupElement && isOutsideTriggerElement) {
                        _this.state.open && _this.trySetOpen(false, e, true);
                    }
                }, {
                    useCapture: true,
                });
            });
        }
    };
    Popup.prototype.componentDidMount = function () {
        this.updateOutsideClickSubscription();
        if (!this.state.open) {
            this.popupDomElement = null;
        }
    };
    Popup.prototype.componentDidUpdate = function () {
        this.updateOutsideClickSubscription();
        if (!this.state.open) {
            this.popupDomElement = null;
        }
    };
    Popup.prototype.componentWillUnmount = function () {
        this.outsideClickSubscription.unsubscribe();
    };
    Popup.prototype.renderComponent = function (_a) {
        var classes = _a.classes, rtl = _a.rtl, accessibility = _a.accessibility;
        var popupContent = this.renderPopupContent(classes.popup, rtl, accessibility);
        return (React.createElement(React.Fragment, null,
            this.renderTrigger(accessibility),
            this.state.open &&
                Popup.isBrowserContext &&
                popupContent &&
                ReactDOM.createPortal(popupContent, document.body)));
    };
    Popup.prototype.renderTrigger = function (accessibility) {
        var _this = this;
        var _a = this.props, children = _a.children, trigger = _a.trigger;
        var triggerElement = childrenExist(children) ? children : trigger;
        return (triggerElement && (React.createElement(Ref, { innerRef: function (domNode) {
                _this.trySetState({ target: domNode });
                _this.triggerDomElement = domNode;
            } }, React.cloneElement(triggerElement, __assign({ onClick: function (e) {
                var rest = [];
                for (var _i = 1; _i < arguments.length; _i++) {
                    rest[_i - 1] = arguments[_i];
                }
                _this.trySetOpen(!_this.state.open, e);
                _.invoke.apply(_, [triggerElement, 'props.onClick', e].concat(rest));
            } }, accessibility.attributes.trigger, accessibility.keyHandlers.trigger)))));
    };
    Popup.prototype.renderPopupContent = function (popupPositionClasses, rtl, accessibility) {
        var _a = this.props, align = _a.align, position = _a.position, offset = _a.offset;
        var target = this.state.target;
        var placement = getPopupPlacement({ align: align, position: position, rtl: rtl });
        var popperModifiers = __assign({}, (offset && {
            offset: { offset: rtl ? applyRtlToOffset(offset, position) : offset },
            keepTogether: { enabled: false },
        }));
        return (target && (React.createElement(Popper, { placement: placement, referenceElement: target, children: this.renderPopperChildren.bind(this, popupPositionClasses, rtl, accessibility), modifiers: popperModifiers })));
    };
    Popup.prototype.trySetOpen = function (newValue, eventArgs, forceChangeEvent) {
        if (forceChangeEvent === void 0) { forceChangeEvent = false; }
        if (this.trySetState({ open: newValue }) || forceChangeEvent) {
            _.invoke(this.props, 'onOpenChange', eventArgs, __assign({}, this.props, { open: newValue }));
        }
    };
    Popup.displayName = 'Popup';
    Popup.className = 'ui-popup';
    Popup.Content = PopupContent;
    Popup.propTypes = __assign({}, commonPropTypes.createCommon({
        animated: false,
        as: false,
        content: 'shorthand',
    }), { accessibility: PropTypes.oneOfType([PropTypes.object, PropTypes.func]), align: PropTypes.oneOf(ALIGNMENTS), defaultOpen: PropTypes.bool, defaultTarget: PropTypes.any, open: PropTypes.bool, onOpenChange: PropTypes.func, position: PropTypes.oneOf(POSITIONS), target: PropTypes.any, trigger: PropTypes.any });
    Popup.defaultProps = {
        accessibility: popupBehavior,
        align: 'start',
        position: 'above',
    };
    Popup.autoControlledProps = ['open', 'target'];
    Popup.isBrowserContext = isBrowser();
    return Popup;
}(AutoControlledComponent));
export default Popup;
